/**
 *
 *  SignUpForm
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 02/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useAppState } from "../../state";
import { useSignUpState } from "../../state/SignUpState";

// Components
import Button from "../Button/Button";
import PhoneCodeInput from "../PhoneCodeInput/PhoneCodeInput";
import DialingCodeSelectorPopup from "../PhoneCodeInput/DialingCodeSelectorPopup/DialingCodeSelectorPopup";
import AppleLogin from "../AppleLogin/AppleLogin";
import GoogleLogin from "../../components/GoogleLogin/GoogleLogin";
import FacebookLogin from "../FacebookLogin/FacebookLogin";

// Icons
import ArrowRightIcon from "../../icons/ArrowRightIcon";
import MailIcon from "../../icons/MailIcon";

// Hooks
import useCountryName from "../../hooks/useCountryName/useCountryName";

// Utils
import {
  formatPhoneNumber,
  getCountryInfo,
  getCountryInfoByPhoneCode,
  getLocationReturnUrl,
  isAndroidDevice,
  isIosDevice,
  isMac,
} from "../../utils";

import signUpDiscountImg from "./sign-up-discount.png";
import signUpDiscountImg2x from "./sign-up-discount@2x.png";
import mastercard from "../../images/payment-method-mastercard.png";
import mastercard2x from "../../images/payment-method-mastercard@2x.png";
import mastercard3x from "../../images/payment-method-mastercard@3x.png";

export default function SignUpForm({
  title = "Sign Up or Login",
  ctaText = "Continue",
  dividerText = "Or continue with:",
  withReferral,
  smallButtons,
  returnTo,
  onNext,
  onSignIn = () => {},
  onCloseClick,
}) {
  const history = useHistory();
  const location = useLocation();

  const {
    checkPhoneNumber,
    verifyPhoneNumber,
    verifyPhoneNumberOrEmailForSimpleLogin,
    checkAppleLogin,
    loginWithApple,
    checkGoogleLogin,
    checkFacebookLogin,
    loginWithGoogle,
    loginWithFacebook,
    clearRegistrationCode,
    error,
    setError,
    isFetching,
  } = useAppState();
  const {
    setAuthState,
    setUserPhoneCode,
    userPhoneCode,
    setUserPhoneNumber,
    userPhoneNumber,
    setUserFirstName,
    setUserFamilyName,
    setUserEmail,
    setIdentityToken,
    setAuthorizationCode,
    setAuthVendor,
    setAccessToken,
  } = useSignUpState();

  const [canSend, setCanSend] = React.useState(false);
  const [phoneCode, setPhoneCode] = React.useState("+1");
  const [countryFlag, setCountryFlag] = React.useState("🇺🇸");
  const [showChooseCodePopup, setShowChooseCodePopup] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("US");
  const [latlng, setLatlng] = useState();
  const [phoneNumber, setPhoneNumber] = useState();
  const [hasError, setHasError] = useState(false);
  const [notificationsConsent, setNotificationsConsent] = useState(true);
  const [termsConsent, setTermsConsent] = useState(true);

  const [, countryShortName, lookupAddress] = useCountryName(latlng);

  const phoneNumberRef = React.useRef();
  const phoneCodeInputRef = React.useRef();
  const isMounted = React.useRef(true); // Create a ref to track if the component is mounted

  useEffect(() => {
    let latLng;

    if (navigator.geolocation /* && !userPhoneCode*/) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (isMounted.current) {
            // Check if the component is still mounted
            latLng = `${pos.coords.latitude},${pos.coords.longitude}`;
            setLatlng(latLng);

            lookupAddress(latLng);
          }
        },
        (err) => {
          console.warn(`ERROR(${err.code}): ${err.message}`);
          if (isMounted.current) {
            // Check if the component is still mounted
            setLatlng(`${34.098907},${-118.327759}`);
          }

          // lookupAddress(latlng);
        }
      );
    }

    return () => {
      isMounted.current = false; // Set the flag to false when unmounted
    };
  }, [/*userPhoneCode, */ lookupAddress]);

  useEffect(() => {
    if (countryShortName) {
      let [phoneCode, countryFlag, countryFlagImage] =
        getCountryInfo(countryShortName);

      setPhoneCode(phoneCode);
      setUserPhoneCode(phoneCode);
      setCountryFlag(
        isAndroidDevice || isMac || isIosDevice ? countryFlag : countryFlagImage
      );
      setSelectedCountry(countryShortName);
    }
  }, [countryShortName]);

  useEffect(() => {
    if (userPhoneCode && userPhoneNumber) {
      let [countryShortName, countryFlag, countryFlagImage] =
        getCountryInfoByPhoneCode(userPhoneCode);

      setPhoneCode(userPhoneCode);
      setPhoneNumber(userPhoneNumber);
      setCountryFlag(
        isAndroidDevice || isMac || isIosDevice ? countryFlag : countryFlagImage
      );
      setSelectedCountry(countryShortName);

      if (validatePhoneNumber(userPhoneNumber)) {
        setCanSend(true);
      }
    }
  }, [userPhoneCode, userPhoneNumber]);

  const handlePhoneCodeSelect = (countryInfo) => {
    if (countryInfo) {
      setPhoneCode(countryInfo.phoneCode);
      setUserPhoneCode(countryInfo.phoneCode);
      setCountryFlag(
        isAndroidDevice || isMac || isIosDevice
          ? countryInfo.emoji
          : countryInfo.image
      );
      setSelectedCountry(countryInfo.code);
      setShowChooseCodePopup(false);
    }
  };

  const handleDialingCodeSelectorPopupClose = () => {
    setShowChooseCodePopup(false);
  };

  const handlePhoneNumberChange = (e) => {
    let value = e.target.value;

    value = formatPhoneNumber(value);

    setPhoneNumber(value);

    setHasError(false);

    if (validatePhoneNumber(value)) {
      setCanSend(true);
    } else {
      setCanSend(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setAuthVendor("");
    setError(null);

    if (phoneCode && phoneNumber) {
      setUserPhoneNumber(phoneNumber);
      setHasError(false);

      checkPhoneNumber(phoneCode, phoneNumber)
        .then((res) => {
          if (res.status === "EXISTING_CUSTOMER") {
            //setAuthState("codeVerification"); //setAuthState("login");

            return verifyPhoneNumberOrEmailForSimpleLogin({
              phoneCode,
              phoneNumber /*, ""*/,
            }).then((res) => ({ isLogin: true }));

            if (!location.pathname.match("/login")) {
              history.push({
                pathname: "/login",
                search: "?authState=login",
                state: { from: returnTo || location.pathname },
              });
            }

            return Promise.reject();
          } else {
            // NEW_CUSTOMER
            // Do we have this data in here?
            if (res.migrationData) {
              return verifyPhoneNumber({
                phoneCode: res.migrationData.phoneCode,
                phoneNumber: res.migrationData.phoneNumber,
                authVendor: "",
              });
            }

            return verifyPhoneNumber({
              phoneCode,
              phoneNumber,
              authVendor: "",
            });
          }
        })
        .then((res) => {
          onNext("", {}, res.isLogin);
        })
        .catch(() => {});
    }
  };

  const handleLoginWithEmail = (e) => {
    e.preventDefault();

    // If there were previous unfinished registration, clean it
    clearRegistrationCode();

    setAuthVendor("email");
    setError(null);

    onNext("email", {}, true);
  };

  const handleLoginWithApple = ({ identityToken, authorizationCode }) => {
    setIdentityToken(identityToken);
    setAuthorizationCode(authorizationCode);
    setHasError(false);
    setError(null);

    checkAppleLogin(identityToken, authorizationCode)
      .then((res) => {
        if (res.existed) {
          loginWithApple(identityToken, authorizationCode)
            .then((res) => {
              onSignIn(res, returnTo, true);
            })
            .catch((err) => {
              if (err) {
                setHasError(true);
              }
            });
        } else {
          // Store user info for later stages
          setUserFirstName(res.firstName);
          setUserFamilyName(res.lastName);
          setUserEmail(res.email);

          // Should be set in onNext
          // setAuthState("setPhoneNumber");

          const returnTo = window.localStorage.getItem(
            "apple-redirect-return-to"
          );

          if (returnTo) {
            // We can't remove it here because of region redirect.
            // We'll remove it in LoginPage
            // window.localStorage.removeItem("apple-redirect-return-to");

            const queryReturnTo = new URLSearchParams(returnTo.split("?")[1]);

            queryReturnTo.append("apple_login", "yes");

            history.replace({
              ...location,
              state: {
                // ...location.state,
                from: `${returnTo.split("?")[0]}?${queryReturnTo.toString()}`,
              },
              search: `${location.search}&return_to=${encodeURIComponent(
                `${returnTo.split("?")[0]}?${queryReturnTo.toString()}`
              )}`,
            });
          }

          onNext("apple");

          return Promise.reject();
        }
      })
      .catch((resp) => {});
  };

  const handleLoginWithGoogle = (credential) => {
    setIdentityToken(credential);
    // setAuthorizationCode(authorizationCode);
    setHasError(false);
    setError(null);

    checkGoogleLogin(credential)
      .then((res) => {
        if (res.existed) {
          loginWithGoogle(credential)
            .then((res) => {
              // Mainly for services
              onSignIn(res, returnTo);
            })
            .catch((err) => {
              if (err) {
                setHasError(true);
              }
            });
        } else {
          // Store user info for later stages
          // We get full name in firstName from BE
          setUserFirstName(res.firstName);
          setUserFamilyName(res.lastName);
          setUserEmail(res.email);

          // Should be set in onNext
          // setAuthState("setPhoneNumber");

          onNext("google", {
            firstName: res.firstName,
            lastName: res.lastName,
            email: res.email,
            token: credential,
          });

          return Promise.reject();
        }
      })
      .catch((resp) => {});
  };

  /**
   * Callback on get login status request sent to Facebook /me
   *
   * @param accessToken
   * @param email
   * @param name
   */
  const handleLoginWithFacebook = ({ accessToken, email, name }) => {
    setAccessToken(accessToken);
    setAuthVendor("facebook");
    setHasError(false);
    setError(null);

    checkFacebookLogin(accessToken)
      .then((res) => {
        if (res.existed) {
          loginWithFacebook(accessToken)
            .then((res) => {
              // Mainly for services
              onSignIn(res, returnTo);
            })
            .catch((err) => {
              if (err) {
                setHasError(true);
              }
            });
        } else {
          // Store user info for later stages
          // We get full name in firstName from Facebook's /me request

          var fullName = name.split(" "),
            firstName = fullName[0],
            familyName = fullName[fullName.length - 1];

          setUserFirstName(firstName);
          setUserFamilyName(familyName);
          setUserEmail(email);

          // Should be set in onNext
          // setAuthState("setPhoneNumber");

          // For Coach Profile
          onNext("facebook", {
            firstName,
            lastName: familyName,
            email,
            token: accessToken,
          });

          return Promise.reject();
        }
      })
      .catch((resp) => {});
  };

  const handleNotificationsConsentChange = useCallback(() => {
    setNotificationsConsent(!notificationsConsent);
  }, [notificationsConsent]);

  const handleTermsConsentChange = useCallback(() => {
    setTermsConsent(!termsConsent);
  }, [termsConsent]);

  return (
    <>
      <div className="tbk-relative">
        <div className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
          {title}
        </div>
        <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
          Enter your phone number to continue.
        </p>
        <form className="tbk-mb-7_" onSubmit={handleSubmit}>
          <div className="tbk-mb-2 tbk-flex">
            <PhoneCodeInput
              ref={phoneCodeInputRef}
              className="tbk-mr-1"
              countryFlag={countryFlag}
              countryShortName={countryShortName}
              phoneCode={phoneCode}
              onClick={() =>
                showChooseCodePopup ? null : setShowChooseCodePopup(true)
              }
            />
            <input
              ref={phoneNumberRef}
              type="tel"
              placeholder="Phone number"
              className="tbk-input-field tbk-text-title-medium tbk-h-7 tbk-grow tbk-pr-2"
              style={{ marginBottom: 0 }}
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              onKeyUp={handlePhoneNumberChange}
            />
          </div>
          <Button
            type="submit"
            className="tb-button tbk-mb-4 tbk-mb-4 tbk-w-full"
            disabled={
              !canSend || !notificationsConsent || !termsConsent || isFetching
            }
            showLoader={isFetching}
            endIcon={canSend ? null : <ArrowRightIcon />}
          >
            {ctaText}
          </Button>
        </form>
        <DialingCodeSelectorPopup
          phoneCodeInputRef={phoneCodeInputRef}
          show={showChooseCodePopup}
          selectedCountry={selectedCountry}
          onSelect={handlePhoneCodeSelect}
          onClose={handleDialingCodeSelectorPopupClose}
        />
      </div>
      <div
        className={`login-or-separator tbk-relative tbk-mt-2 tbk-text-center${
          withReferral ? "" : " tbk-mb-7"
        }`}
      >
        <span className="login-or-separator-text tbk-inline-block tbk-bg-basic-white tbk-py-0 tbk-px-2">
          {dividerText}
        </span>
        <hr className="tbk-mx-auto tbk-mb-4 tbk--mt-1.5 tbk-border-grey-inactive" />
        <div
          className={`tbk-grid tbk-gap-1${
            smallButtons ? " tbk-grid-cols-2" : ""
          }`}
        >
          {process.env.REACT_APP_REGION == "GB" || true ? (
            <Button
              variant="secondary"
              className={`tbk-w-full !tbk-py-1.5${
                smallButtons ? " tbk-order-4 tbk-col-span-2" : ""
              }`}
              disabled={isFetching || !notificationsConsent || !termsConsent}
              startIcon={
                smallButtons ? (
                  <MailIcon className="tbk-text-basic-white" />
                ) : null
              }
              onClick={handleLoginWithEmail}
            >
              <div className="tbk-flex tbk-items-center">
                {smallButtons ? (
                  <span className={"tbk-text-main"}>Log in with Email</span>
                ) : (
                  <>
                    <MailIcon className="tbk-mr-1 tbk-text-basic-white" />
                    Email
                  </>
                )}
              </div>
            </Button>
          ) : null}

          <AppleLogin
            clientId={process.env.REACT_APP_APPLE_SIGNIN_CLIENT_ID}
            redirectURI={process.env.REACT_APP_APPLE_SIGNIN_AUTH_REDIRECT_URL}
            returnTo={returnTo || getLocationReturnUrl(location)}
            stateFrom={location && location.state && location.state.from}
            responseType="code id_token"
            responseMode="form_post"
            scope="name email"
            callback={handleLoginWithApple}
            designProp={{
              height: 52,
              width: 375,
              border_radius: 16,
              scale: 2,
            }}
            disabled={isFetching || !notificationsConsent || !termsConsent}
            className="tbk-w-full"
            size={smallButtons ? "small" : undefined}
          />

          {/*<FacebookLogin
            socialId={process.env.REACT_APP_FACEBOOK_APP_ID}
            language="en_US"
            scope="public_profile,email"
            responseHandler={handleLoginWithFacebook}
            xfbml={true}
            fields="name,email,picture"
            version="v2.5"
            disabled={isFetching || !notificationsConsent || !termsConsent}
            className="tbk-w-full"
            buttonText="Facebook"
            size={smallButtons ? "small" : undefined}
          />*/}

          <GoogleLogin
            disabled={isFetching || !notificationsConsent || !termsConsent}
            onSuccess={(credentialResponse) => {
              setHasError(false);
              setAuthVendor("google");

              handleLoginWithGoogle(credentialResponse.credential);
              console.log(credentialResponse);
            }}
            onError={() => {
              setHasError(true);

              console.log("Google login Failed");
            }}
            logo_alignment={"center"}
            containerProps={{
              width: "100%",
              height: "54",
            }}
            // width={}
            height={54}
            size={smallButtons ? "small" : undefined}
          />
        </div>
        {withReferral ? (
          <>
            <img
              className="tbk-my-4"
              src={signUpDiscountImg}
              srcSet={`${signUpDiscountImg} 1x, ${signUpDiscountImg2x} 2x`}
              alt="Sign Up For Free & Get $20 Discount"
            />
          </>
        ) : null}
        <>
          <div className="tbk-mt-4 tbk-mb-1">
            <input
              id={"notificationsConsent"}
              className="tbk-checkbox-small tbk-checkbox-small-grey"
              type="checkbox"
              name="notificationsConsent"
              checked={notificationsConsent}
              onChange={handleNotificationsConsentChange}
            />
            <label
              htmlFor={"notificationsConsent"}
              className="tbk-flex tbk-h-3 tbk-items-center tbk-pl-4"
            >
              <div className="tbk-text-small tbk-text-left tbk-text-blue-grey">
                I agree to receive TruBe notifications via sms and email.
              </div>
            </label>
          </div>
          <div className="tbk-mb-5_">
            <input
              id={"termsConsent"}
              className="tbk-checkbox-small tbk-checkbox-small-grey"
              type="checkbox"
              name="termsConsent"
              checked={termsConsent}
              onChange={handleTermsConsentChange}
            />
            <label
              htmlFor={"termsConsent"}
              className="tbk-flex tbk-h-3 tbk-items-center tbk-pl-4"
            >
              <div className="tbk-text-small tbk-text-left tbk-text-blue-grey">
                I agree to TruBe{" "}
                <a
                  className="tbk-text-blue-grey tbk-underline"
                  href="/terms/Terms of Use and Booking.pdf"
                  target="_blank"
                >
                  Terms & Conditions.
                </a>
              </div>
            </label>
          </div>
        </>

        {hasError ? (
          <div className="tbk-text-main tbk-mt-1 tbk-mb-2 tbk-text-center tbk-text-coral">
            {error ? error.message : "Unknown error has occurred"}
          </div>
        ) : null}
      </div>

      <p className="tbk-text-small tbk-hidden tbk-text-center tbk-text-grey">
        Registering to this website, you accept our{" "}
        <a href="/terms/Terms of Use and Booking.pdf" target="_blank">
          Terms of Use
        </a>{" "}
        and{" "}
        <a href="/terms/TruBe - US Privacy Policy.pdf" target="_blank">
          Privacy Policy
        </a>
      </p>
    </>
  );
}

function validatePhoneNumber(phone) {
  return phone && phone.replace(/\s/g, "").length >= 9;
}
